<template>
    <div class="prog">
        <div class="prog-top">
            <span class="prog-lbl">{{ $t('progression.show_only') }}</span>
            <button v-for="day in weekdayOptions" :key="day.value" type="button" class="prog-wd"
                :class="{ active: weekday === day.value, off: !daysWithSession.has(day.value) }"
                @click="weekday = day.value">
                {{ day.label }}
            </button>
            <div class="prog-nav">
                <button-tooltip bottom :tooltip="$t('progression.previous_week')">
                    <template #activator="{ on, attrs }">
                        <button type="button" class="prog-step" :disabled="windowStart <= MIN_OFFSET" v-bind="attrs"
                            v-on="on" @click="windowStart--">
                            <v-icon size="16">mdi-chevron-left</v-icon>
                        </button>
                    </template>
                </button-tooltip>
                <span class="prog-nav-lbl">{{ rangeLabel }}</span>
                <button-tooltip bottom :tooltip="$t('progression.next_week')">
                    <template #activator="{ on, attrs }">
                        <button type="button" class="prog-step" :disabled="windowStart >= MAX_OFFSET - VISIBLE + 1"
                            v-bind="attrs" v-on="on" @click="windowStart++">
                            <v-icon size="16">mdi-chevron-right</v-icon>
                        </button>
                    </template>
                </button-tooltip>
                <button type="button" class="prog-today" :disabled="windowStart === DEFAULT_START"
                    @click="windowStart = DEFAULT_START">
                    {{ $t('progression.current') }}
                </button>
            </div>
        </div>

        <skeleton-loader v-if="loading" type="table" />
        <div v-else class="prog-wrap">
            <table class="prog-t">
                <thead>
                    <tr>
                        <th class="sticky">{{ $t('common.exercises') }}</th>
                        <th v-for="week in weeks" :key="week.offset" class="prog-c" :class="{ now: week.offset === 0 }">
                            <div class="prog-wk">
                                <span>{{ week.label }}</span>
                                <match-day-badge v-if="week.matchDay" :value="week.matchDay" />
                                <input class="prog-wk-name" :value="nameDrafts[week.weekStart]"
                                    :placeholder="$t('progression.add_name')" :maxlength="WEEK_NAME_MAX"
                                    @input="setDraft(week.weekStart, $event.target.value)"
                                    @keydown.enter.prevent="$event.target.blur()"
                                    @keydown.esc.prevent="cancelName(week.weekStart, $event)"
                                    @blur="commitName(week.weekStart)" />
                            </div>
                            <div class="prog-wk-date">
                                <span v-if="week.offset === 0" class="prog-tag now">{{ $t('progression.current') }}</span>
                                <span v-else>{{ relativeLabel(week.offset) }}</span>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!rows.length">
                        <td :colspan="weeks.length + 1" class="prog-empty">
                            {{ $t('progression.free_day', { day: weekdayName }) }}
                        </td>
                    </tr>
                    <tr v-for="row in rows" :key="row.key">
                        <td class="sticky">
                            <div class="prog-ex">{{ row.name }}</div>
                            <div class="prog-ex-b">{{ $tc('common.blocks', 1) }} {{ row.block }}</div>
                        </td>
                        <td v-for="week in weeks" :key="week.offset" class="prog-c" :class="{ now: week.offset === 0 }">
                            <template v-if="cell(row.key, week.offset)">
                                <div class="prog-v">
                                    {{ cell(row.key, week.offset).sets }} × {{ cell(row.key, week.offset).reps }}
                                    <span v-for="chip in diffChips(row.key, week.offset)" :key="chip.text" class="prog-d"
                                        :class="chip.up ? 'up' : 'down'">{{ chip.text }}</span>
                                </div>
                                <div class="prog-kg">
                                    {{ weightLabel(cell(row.key, week.offset).weights) }}
                                    <span v-if="weightDiff(row.key, week.offset)" class="prog-d"
                                        :class="weightDiff(row.key, week.offset).up ? 'up' : 'down'">
                                        {{ weightDiff(row.key, week.offset).text }}
                                    </span>
                                </div>
                                <div class="prog-ctrl">
                                    <span>S</span>
                                    <button type="button" @click="adjust(row, week, 'sets', -1)">−</button>
                                    <button type="button" @click="adjust(row, week, 'sets', 1)">+</button>
                                    <span>R</span>
                                    <button type="button" @click="adjust(row, week, 'reps', -1)">−</button>
                                    <button type="button" @click="adjust(row, week, 'reps', 1)">+</button>
                                    <span>KG</span>
                                    <button type="button" @click="adjust(row, week, 'kg', -WEIGHT_STEP)">−</button>
                                    <button type="button" @click="adjust(row, week, 'kg', WEIGHT_STEP)">+</button>
                                </div>
                            </template>
                            <span v-else class="prog-na">—</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { db } from '@/utils/Firebase'
import { clearBlocksAssignedRoutine, exerciseKey } from '@/utils/Exercises'
import { matchDayLabel } from '@/utils/MatchDay'
import { WEEK_NAME_MAX, loadWeekNames, saveWeekName } from '@/utils/WeekNames'
import { pushHistory, registerHistoryFlusher } from '@/utils/History'
import { CALENDAR_HISTORY } from '@/utils/CalendarHistory'
import ButtonTooltip from '@/components/common/ButtonTooltip'
import SkeletonLoader from '@/components/common/SkeletonLoader'
import MatchDayBadge from '@/components/calendar/MatchDayBadge'

const MIN_OFFSET = -3
const MAX_OFFSET = 3
const VISIBLE = 4
const DEFAULT_START = -1
const WEIGHT_STEP = 2.5
const PERSIST_DELAY = 600

export default {
    name: 'CalendarProgression',
    components: { ButtonTooltip, SkeletonLoader, MatchDayBadge },
    props: {
        scope: {
            type: Object,
            default: null,
        },
        coachId: {
            type: String,
            default: null,
        },
        routineNumber: {
            type: Number,
            default: 1,
        },
        revision: {
            type: Number,
            default: 0,
        },
        track: {
            type: Function,
            required: true,
        },
        persist: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            MIN_OFFSET,
            MAX_OFFSET,
            VISIBLE,
            DEFAULT_START,
            WEIGHT_STEP,
            WEEK_NAME_MAX,
            loading: false,
            docs: [],
            weekday: null,
            windowStart: DEFAULT_START,
            names: {},
            nameDrafts: {},
        }
    },
    computed: {
        baseWeek() {
            return this.$moment().startOf('isoWeek')
        },
        weekdayOptions(vm = this) {
            return [1, 2, 3, 4, 5, 6, 0].map(value => ({
                value,
                label: vm.$moment().day(value).format('ddd').replace('.', '').toUpperCase(),
            }))
        },
        weekdayName(vm = this) {
            return vm.weekday === null ? '' : vm.$moment().day(vm.weekday).format('dddd')
        },
        routines(vm = this) {
            return vm.docs.filter(doc => (doc.routineNumber || 1) === vm.routineNumber)
        },
        routinesByDate(vm = this) {
            const map = new Map()
            vm.routines.forEach(doc => {
                if (doc.type !== 'competence' && doc.rawDate?.full) map.set(doc.rawDate.full, doc)
            })
            return map
        },
        competenceDates(vm = this) {
            return vm.routines.filter(doc => doc.type === 'competence').map(doc => doc.rawDate?.full).filter(Boolean).sort()
        },
        daysWithSession(vm = this) {
            const days = new Set()
            for (let offset = MIN_OFFSET; offset <= MAX_OFFSET; offset++) {
                vm.weekdayOptions.forEach(day => {
                    if (vm.routinesByDate.has(vm.dateFor(offset, day.value))) days.add(day.value)
                })
            }
            return days
        },
        weeks(vm = this) {
            return Array.from({ length: VISIBLE }, (_, i) => {
                const offset = vm.windowStart + i
                const date = vm.dateFor(offset)
                return {
                    offset,
                    date,
                    weekStart: vm.weekStartFor(offset),
                    label: vm.shortDate(date),
                    matchDay: matchDayLabel(date, vm.competenceDates),
                }
            })
        },
        rangeLabel(vm = this) {
            const first = vm.weeks[0]
            const last = vm.weeks[vm.weeks.length - 1]
            return first && last ? `${vm.shortDate(first.date)} – ${vm.shortDate(last.date)}` : ''
        },
        rows(vm = this) {
            const rows = []
            const seen = new Set()
            vm.weeks.forEach(week => {
                vm.exerciseEntries(vm.routinesByDate.get(week.date)).forEach(entry => {
                    if (seen.has(entry.key)) return
                    seen.add(entry.key)
                    rows.push({ key: entry.key, name: entry.exercise.name, block: entry.blockIndex + 1 })
                })
            })
            return rows
        },
    },
    created() {
        const vm = this
        vm._pending = {}
        vm._unregisterFlusher = registerHistoryFlusher(CALENDAR_HISTORY, () => vm.flushPending())
    },
    mounted() {
        this.load()
    },
    beforeDestroy() {
        this._unregisterFlusher()
        this.flushPending()
    },
    methods: {
        weekStartFor(offset) {
            return this.baseWeek.clone().add(offset, 'week').format('YYYY-MM-DD')
        },
        dateFor(offset, weekday = this.weekday) {
            const day = weekday === null ? 1 : weekday
            return this.baseWeek.clone().add(offset, 'week').add(day === 0 ? 6 : day - 1, 'day').format('YYYY-MM-DD')
        },
        shortDate(date) {
            return this.$moment(date).format('ddd D MMM').replace(/\./g, '').toUpperCase()
        },
        relativeLabel(offset) {
            const n = Math.abs(offset)
            return this.$t(offset < 0 ? 'progression.weeks_ago' : 'progression.weeks_ahead', { n })
        },
        async load() {
            const vm = this
            const scope = vm.scope
            if (!scope) return
            vm.loading = true
            try {
                const from = vm.baseWeek.clone().add(MIN_OFFSET - 1, 'week').subtract(10, 'day').format('YYYY-MM-DD')
                const to = vm.baseWeek.clone().add(MAX_OFFSET + 1, 'week').add(4, 'day').format('YYYY-MM-DD')
                let query
                if (scope.collection === 'assignedRoutines') {
                    query = db.collection('assignedRoutines').where('studentId', '==', scope.studentId)
                } else {
                    query = db.collection('groupAssignedRoutines')
                    if (scope.teamId) query = query.where('teamId', '==', scope.teamId)
                    query = query.where('groupId', '==', scope.groupId)
                }
                const snap = await query.where('rawDate.full', '>=', from).where('rawDate.full', '<=', to).get()
                vm.docs = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }))
                const weekStarts = Array.from({ length: MAX_OFFSET - MIN_OFFSET + 1 }, (_, i) => vm.weekStartFor(MIN_OFFSET + i))
                vm.names = await loadWeekNames(scope, weekStarts)
                vm.nameDrafts = { ...vm.names }
                if (vm.weekday === null) vm.weekday = vm.defaultWeekday()
            } catch (error) {
                console.error('[progression] load', error)
                vm.docs = []
            } finally {
                vm.loading = false
            }
        },
        defaultWeekday() {
            const vm = this
            const today = vm.$moment().day()
            if (vm.daysWithSessionFor(today)) return today
            const first = vm.weekdayOptions.find(day => vm.daysWithSessionFor(day.value))
            return first ? first.value : today
        },
        daysWithSessionFor(weekday) {
            for (let offset = MIN_OFFSET; offset <= MAX_OFFSET; offset++) {
                if (this.routinesByDate.has(this.dateFor(offset, weekday))) return true
            }
            return false
        },
        exerciseEntries(routine) {
            const entries = []
            ;(routine?.blocks || []).forEach((block, blockIndex) => {
                if (block?.isCircuit) return
                const counts = {}
                ;(block?.exercises || []).forEach((exercise, exerciseIndex) => {
                    if (exercise?.type === 'rest' || !exercise?.config?.sets?.length) return
                    const id = exerciseKey(exercise) || exercise.name
                    counts[id] = (counts[id] || 0) + 1
                    entries.push({ key: `${blockIndex}|${id}|${counts[id]}`, blockIndex, exerciseIndex, exercise })
                })
            })
            return entries
        },
        locate(routine, key) {
            return this.exerciseEntries(routine).find(entry => entry.key === key) || null
        },
        weightOf(set) {
            const weight = (set?.data || []).find(item => item.short_name === 'short_weight')
            return Number(weight?.value) || 0
        },
        cell(key, offset) {
            const vm = this
            const entry = vm.locate(vm.routinesByDate.get(vm.dateFor(offset)), key)
            if (!entry) return null
            const sets = entry.exercise.config.sets
            const primary = sets[0]?.data?.[0]
            const unit = primary?.short_name && primary.short_name !== 'short_reps' ? ` ${vm.$t(`exercises.${primary.short_name}`)}` : ''
            return {
                sets: sets.length,
                reps: `${Number(primary?.value) || 0}${unit}`,
                repsValue: Number(primary?.value) || 0,
                kg: Math.max(0, ...sets.map(vm.weightOf)),
                weights: sets.map(vm.weightOf),
            }
        },
        diffChips(key, offset) {
            const vm = this
            const current = vm.cell(key, offset)
            const previous = vm.cell(key, offset - 1)
            if (!current || !previous) return []
            const chips = []
            const sets = current.sets - previous.sets
            const reps = current.repsValue - previous.repsValue
            if (sets) chips.push({ up: sets > 0, text: `${sets > 0 ? '+' : '−'}${Math.abs(sets)} s` })
            if (reps) chips.push({ up: reps > 0, text: `${reps > 0 ? '+' : '−'}${Math.abs(reps)} r` })
            return chips
        },
        formatKg(value) {
            return Number(value).toLocaleString(this.$i18n.locale, { maximumFractionDigits: 2 })
        },
        weightLabel(weights) {
            return weights.some(weight => weight > 0) ? `${weights.map(weight => this.formatKg(weight)).join(' / ')} kg` : this.$t('progression.no_weight')
        },
        weightDiff(key, offset) {
            const vm = this
            const current = vm.cell(key, offset)
            const previous = vm.cell(key, offset - 1)
            if (!current || !previous) return null
            const diff = Math.round((current.kg - previous.kg) * 100) / 100
            if (!diff) return null
            return { up: diff > 0, text: `${diff > 0 ? '+' : '−'}${vm.formatKg(Math.abs(diff))} kg` }
        },
        adjust(row, week, field, delta) {
            const vm = this
            const routine = vm.routinesByDate.get(week.date)
            const entry = vm.locate(routine, row.key)
            if (!entry) return
            const blocks = cloneDeep(routine.blocks)
            const sets = blocks[entry.blockIndex].exercises[entry.exerciseIndex].config.sets
            if (field === 'sets') {
                if (delta > 0) sets.push(cloneDeep(sets[sets.length - 1]))
                else if (sets.length > 1) sets.pop()
                else return vm.toast(vm.$t('common.cannot_delete_last_set'), 'error')
            } else if (field === 'reps') {
                sets.forEach(set => {
                    if (set.data?.[0]) set.data[0].value = Math.max(1, (Number(set.data[0].value) || 0) + delta)
                })
            } else {
                sets.forEach(set => {
                    if (!set.data) set.data = []
                    let weight = set.data.find(item => item.short_name === 'short_weight')
                    if (!weight) {
                        weight = { short_name: 'short_weight', value: 0 }
                        set.data.push(weight)
                    }
                    weight.value = Math.max(0, Math.round(((Number(weight.value) || 0) + delta) * 100) / 100)
                })
            }
            routine.blocks = blocks
            vm.schedulePersist(routine, row, week)
        },
        schedulePersist(routine, row, week) {
            const vm = this
            const pending = vm._pending[routine.id]
            if (pending) clearTimeout(pending.timer)
            vm._pending[routine.id] = { routine, row, week, timer: setTimeout(() => vm.runPending(routine.id), PERSIST_DELAY) }
        },
        runPending(id) {
            const vm = this
            const pending = vm._pending[id]
            if (!pending) return Promise.resolve()
            clearTimeout(pending.timer)
            delete vm._pending[id]
            const { routine, row, week } = pending
            const label = vm.$t('history.load_of', { name: `«${row.name}»`, date: week.label })
            return vm.track(label, [week.date], () => vm.persist(routine.id, clearBlocksAssignedRoutine(routine.blocks, false)), `prog|${routine.id}|${row.key}`)
                .catch(error => {
                    console.error('[progression] persist', error)
                    vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                })
        },
        flushPending() {
            const vm = this
            return Promise.all(Object.keys(vm._pending).map(id => vm.runPending(id)))
        },
        setDraft(weekStart, value) {
            this.$set(this.nameDrafts, weekStart, value)
        },
        cancelName(weekStart, event) {
            const vm = this
            vm.$set(vm.nameDrafts, weekStart, vm.names[weekStart] || '')
            vm._skipBlur = weekStart
            event.target.blur()
        },
        async commitName(weekStart) {
            const vm = this
            if (vm._skipBlur === weekStart) {
                vm._skipBlur = null
                return
            }
            const previous = vm.names[weekStart] || ''
            const next = String(vm.nameDrafts[weekStart] || '').trim().toUpperCase().slice(0, WEEK_NAME_MAX)
            vm.$set(vm.nameDrafts, weekStart, next)
            if (next === previous) return
            const apply = async value => {
                await saveWeekName(vm.scope, weekStart, value, vm.coachId)
                vm.$set(vm.names, weekStart, value)
                vm.$set(vm.nameDrafts, weekStart, value)
            }
            try {
                await apply(next)
                pushHistory(CALENDAR_HISTORY, {
                    label: vm.$t('history.week_name', { date: vm.shortDate(weekStart) }),
                    undo: () => apply(previous),
                    redo: () => apply(next),
                })
            } catch (error) {
                console.error('[progression] week name', error)
                vm.$set(vm.nameDrafts, weekStart, previous)
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
            }
        },
    },
    watch: {
        revision() {
            this.load()
        },
        scope() {
            this.weekday = null
            this.windowStart = DEFAULT_START
            this.load()
        },
        routineNumber() {
            this.load()
        },
    },
}
</script>

<style scoped>
.prog {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px 20px 96px;
}

.prog-top {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.prog-lbl {
    margin-right: 4px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.prog-wd {
    height: 30px;
    min-width: 48px;
    padding: 0 10px;
    border-radius: 20px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    color: var(--v-txtSecondary-base);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
}

.prog-wd:hover {
    border-color: var(--v-outline2-base);
    color: var(--v-txtPrimary-base);
}

.prog-wd.off {
    opacity: .5;
}

.prog-wd.active {
    opacity: 1;
    background: rgba(102, 204, 204, .12);
    border-color: rgba(51, 153, 153, .4);
    color: var(--v-accent-base);
}

.theme--dark .prog-wd.active {
    color: var(--v-secondary-base);
    border-color: rgba(102, 204, 204, .4);
}

.prog-nav {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
}

.prog-nav-lbl {
    min-width: 150px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
    color: var(--v-txtSecondary-base);
}

.prog-step,
.prog-today {
    height: 30px;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
}

.prog-step {
    width: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.prog-step .v-icon {
    color: inherit !important;
}

.prog-today {
    padding: 0 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
}

.prog-step:hover:not(:disabled),
.prog-today:hover:not(:disabled) {
    color: var(--v-accent-base);
    border-color: var(--v-outline2-base);
}

.theme--dark .prog-step:hover:not(:disabled),
.theme--dark .prog-today:hover:not(:disabled) {
    color: var(--v-primary-base);
}

.prog-step:disabled,
.prog-today:disabled {
    opacity: .5;
    cursor: default;
}

.prog-wrap {
    overflow: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 14px;
    background: var(--v-surface-base);
}

.prog-t {
    width: 100%;
    min-width: 760px;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;
}

.prog-t th,
.prog-t td {
    padding: 10px 12px;
    text-align: left;
    vertical-align: top;
    border-bottom: 1px solid var(--v-outline-base);
    border-right: 1px solid var(--v-outline-base);
}

.prog-t tr > *:last-child {
    border-right: none;
}

.prog-t tbody tr:last-child td {
    border-bottom: none;
}

.prog-t thead th {
    background: var(--v-surface2-base);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    white-space: nowrap;
    color: var(--v-txtMuted-base);
}

.prog-t .sticky {
    position: sticky;
    left: 0;
    z-index: 2;
    min-width: 210px;
    max-width: 240px;
    background: var(--v-surface-base);
}

.prog-t thead .sticky {
    z-index: 3;
    background: var(--v-surface2-base);
}

.prog-c {
    min-width: 210px;
}

.prog-c.now {
    background: rgba(102, 204, 204, .05);
}

.prog-wk {
    display: flex;
    align-items: center;
    gap: 6px;
}

.prog-wk-name {
    width: 100%;
    min-width: 0;
    height: 22px;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px dashed transparent;
    background: transparent;
    outline: none;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
    color: var(--v-txtPrimary-base);
}

.prog-wk-name::placeholder {
    font-weight: 600;
    letter-spacing: .02em;
    text-transform: none;
    color: var(--v-txtMuted-base);
}

.prog-wk-name:hover {
    border-color: var(--v-outline2-base);
}

.prog-wk-name:focus {
    border-style: solid;
    border-color: var(--v-accent-base);
    background: var(--v-surface-base);
}

.theme--dark .prog-wk-name:focus {
    border-color: var(--v-primary-base);
}

.prog-wk-date {
    margin-top: 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: .06em;
    color: var(--v-txtMuted-base);
}

.prog-tag {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 9px;
    letter-spacing: .06em;
}

.prog-tag.now {
    background: rgba(102, 204, 204, .15);
    color: var(--v-accent-base);
}

.theme--dark .prog-tag.now {
    color: var(--v-secondary-base);
}

.prog-ex {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: var(--v-txtPrimary-base);
}

.prog-ex-b {
    margin-top: 2px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--v-txtMuted-base);
}

.prog-v {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.prog-kg {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.prog-d {
    display: inline-block;
    margin-left: 6px;
    padding: 2px 6px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    vertical-align: 2px;
}

.prog-d.up {
    background: rgba(0, 153, 102, .15);
    color: #009966;
}

.theme--dark .prog-d.up {
    color: #66CC99;
}

.prog-d.down {
    background: rgba(232, 92, 92, .15);
    color: #E85C5C;
}

.prog-ctrl {
    display: flex;
    align-items: center;
    gap: 3px;
    margin-top: 6px;
    opacity: 0;
    transition: opacity .12s;
}

.prog-c:hover .prog-ctrl,
.prog-c:focus-within .prog-ctrl {
    opacity: 1;
}

.prog-ctrl span {
    padding: 0 2px 0 4px;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.prog-ctrl span:first-child {
    padding-left: 0;
}

.prog-ctrl button {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface2-base);
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 700;
}

.prog-ctrl button:hover {
    color: var(--v-accent-base);
    border-color: rgba(51, 153, 153, .4);
}

.theme--dark .prog-ctrl button:hover {
    color: var(--v-primary-base);
    border-color: rgba(204, 255, 51, .4);
}

.prog-na {
    color: var(--v-txtMuted-base);
}

.prog-empty {
    padding: 28px !important;
    text-align: center !important;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}
</style>
